import { useEffect, useState } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { extractLocationCards, type BankLoanTypeLocations, type LocationCard } from '@/lib/bankLoanTypeLocations';
import { extractBankLogoMap, type BankLogoMap } from '@/lib/bankLogos';

export interface AppConfig {
  maxFileUploadSizeMB: number;
  maxFileUploads: number;
  allowedFileTypes: string[];
  // Training-specific upload limits (appConfig/trainingUpload)
  trainingMaxFileUploadSizeMB: number;
  trainingMaxFileUploads: number;
  trainingAllowedFileTypes: string[];
  banks: string[];
  products: string[];
  // appConfig/main.bankLoanTypeLocations — stored as { [lender]: { [loanType]: string[] of locations } }
  bankLoanTypeLocations: BankLoanTypeLocations;
  // Full admin-curated city list from appConfig/main (whichever field is shaped like { key, label, emoji }[])
  locationCards: LocationCard[];
  // Bank name -> logo URL (Firebase Storage bank_logos/...), from appConfig/main
  bankLogoUrls: BankLogoMap;
}

const DEFAULT_CONFIG: AppConfig = {
  maxFileUploadSizeMB: 10,
  maxFileUploads: 10,
  allowedFileTypes: ['image/*', '.pdf', '.doc', '.docx'],
  // Training files: video up to 500 MB, one file per material
  trainingMaxFileUploadSizeMB: 500,
  trainingMaxFileUploads: 1,
  trainingAllowedFileTypes: ['video/*', 'image/*', '.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xlsx', '.xls'],
  banks: ['HDFC Bank', 'ICICI Bank', 'SBI', 'Axis Bank', 'Kotak Mahindra Bank'],
  products: ['Home Loan', 'Loan Against Property (LAP)', 'Personal Loan', 'Business Loan', 'Car Loan'],
  bankLoanTypeLocations: {},
  locationCards: [],
  bankLogoUrls: {},
};

export function useAppConfig() {
  const [config, setConfig] = useState<AppConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fileUploadRef = doc(db, 'appConfig', 'fileUpload');
    const productInfoRef = doc(db, 'appConfig', 'productInfo');
    const trainingUploadRef = doc(db, 'appConfig', 'trainingUpload');
    const mainRef = doc(db, 'appConfig', 'main');

    // Fetch all configs initially
    Promise.all([
      getDoc(fileUploadRef),
      getDoc(productInfoRef),
      getDoc(trainingUploadRef),
      getDoc(mainRef),
    ])
      .then(([fileUploadSnap, productInfoSnap, trainingUploadSnap, mainSnap]) => {
        const fileUploadData = fileUploadSnap.exists() ? fileUploadSnap.data() : {};
        const productInfoData = productInfoSnap.exists() ? productInfoSnap.data() : {};
        const trainingUploadData = trainingUploadSnap.exists() ? trainingUploadSnap.data() : {};
        const mainData = mainSnap.exists() ? mainSnap.data() : {};

        setConfig({
          maxFileUploadSizeMB: fileUploadData.maxFileUploadSizeMB ?? DEFAULT_CONFIG.maxFileUploadSizeMB,
          maxFileUploads: fileUploadData.maxFileUploads ?? DEFAULT_CONFIG.maxFileUploads,
          allowedFileTypes: fileUploadData.allowedFileTypes ?? DEFAULT_CONFIG.allowedFileTypes,
          trainingMaxFileUploadSizeMB: trainingUploadData.maxFileUploadSizeMB ?? DEFAULT_CONFIG.trainingMaxFileUploadSizeMB,
          trainingMaxFileUploads: trainingUploadData.maxFileUploads ?? DEFAULT_CONFIG.trainingMaxFileUploads,
          trainingAllowedFileTypes: trainingUploadData.allowedFileTypes ?? DEFAULT_CONFIG.trainingAllowedFileTypes,
          banks: productInfoData.banks ?? DEFAULT_CONFIG.banks,
          products: productInfoData.products ?? DEFAULT_CONFIG.products,
          bankLoanTypeLocations: mainData.bankLoanTypeLocations ?? DEFAULT_CONFIG.bankLoanTypeLocations,
          locationCards: extractLocationCards(mainData),
          bankLogoUrls: extractBankLogoMap(mainData),
        });
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching app config:', error);
        setLoading(false);
      });

    // Listen for real-time updates on all documents
    const unsubscribeFileUpload = onSnapshot(
      fileUploadRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setConfig(prev => ({
            ...prev,
            maxFileUploadSizeMB: data.maxFileUploadSizeMB ?? DEFAULT_CONFIG.maxFileUploadSizeMB,
            maxFileUploads: data.maxFileUploads ?? DEFAULT_CONFIG.maxFileUploads,
            allowedFileTypes: data.allowedFileTypes ?? DEFAULT_CONFIG.allowedFileTypes,
          }));
        }
      },
      (error) => {
        console.error('Error listening to file upload config:', error);
      }
    );

    const unsubscribeProductInfo = onSnapshot(
      productInfoRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setConfig(prev => ({
            ...prev,
            banks: data.banks ?? DEFAULT_CONFIG.banks,
            products: data.products ?? DEFAULT_CONFIG.products,
          }));
        }
      },
      (error) => {
        console.error('Error listening to product info:', error);
      }
    );

    const unsubscribeTrainingUpload = onSnapshot(
      trainingUploadRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setConfig(prev => ({
            ...prev,
            trainingMaxFileUploadSizeMB: data.maxFileUploadSizeMB ?? DEFAULT_CONFIG.trainingMaxFileUploadSizeMB,
            trainingMaxFileUploads: data.maxFileUploads ?? DEFAULT_CONFIG.trainingMaxFileUploads,
            trainingAllowedFileTypes: data.allowedFileTypes ?? DEFAULT_CONFIG.trainingAllowedFileTypes,
          }));
        }
      },
      (error) => {
        console.error('Error listening to training upload config:', error);
      }
    );

    const unsubscribeMain = onSnapshot(
      mainRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setConfig(prev => ({
            ...prev,
            bankLoanTypeLocations: data.bankLoanTypeLocations ?? DEFAULT_CONFIG.bankLoanTypeLocations,
            locationCards: extractLocationCards(data),
            bankLogoUrls: extractBankLogoMap(data),
          }));
        }
      },
      (error) => {
        console.error('Error listening to main config:', error);
      }
    );

    return () => {
      unsubscribeFileUpload();
      unsubscribeProductInfo();
      unsubscribeTrainingUpload();
      unsubscribeMain();
    };
  }, []);

  return { config, loading };
}
